import { takeUntil } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { ComoService } from '@core/services/como/como.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { ComoAttachUserResponse } from '@core/models/como.model';
import { routeComoRegistrationAfterAttach } from '@shared/modals/como/como-registration-modal/como-registration-route.helper';

@Component({
  selector: 'app-otp-step',
  templateUrl: './otp-step.component.html',
  standalone: false,
})
export class OtpStepComponent extends DestroyerBase {
  @Input() phone?: string;

  @Output() onClose = new EventEmitter<Event>();
  @Output() goToPreviousStep = new EventEmitter<void>();
  @Output() goToNextStep = new EventEmitter<void>();
  @Output() goToComplete = new EventEmitter<void>();

  form: UntypedFormGroup;
  isValidating = false;
  errorMessage?: string;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly comoService: ComoService,
    private readonly analyticsService: AnalyticsService,
    private readonly authStoreSelector: AuthStoreSelector,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly marketplaceService: MarketplaceService,
  ) {
    super();
    this.form = this.formBuilder.group({
      ['authCode']: ['', [Validators.required]],
    });

    this.form.controls['authCode'].valueChanges
      .pipe(takeUntil(this._destroyerRef))
      .subscribe(() => {
        this.errorMessage = undefined;
      });
  }

  onSubmit(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const authCode = this.form.controls['authCode']?.value;

    if (this.form.invalid || this.isValidating || !authCode || !this.phone) {
      return;
    }
    this.analyticsService.trackLoyaltyButtonClick('como', 'submit_otp');
    this.isValidating = true;

    this.comoService.linkComoUser$({ phoneOrEmail: this.phone, code: authCode }).subscribe({
      next: response => this.afterAttach(response),
      error: error => this.handleErrorResponse(error.error),
      complete: () => (this.isValidating = false),
    });
  }

  onGoToPreviousStep(): void {
    this.goToPreviousStep.next();
  }

  private afterAttach(response: ComoAttachUserResponse): void {
    const nextStep = routeComoRegistrationAfterAttach(response, {
      comoService: this.comoService,
      authStoreSelector: this.authStoreSelector,
      marketplaceSelector: this.marketplaceSelector,
      marketplaceService: this.marketplaceService,
      analyticsService: this.analyticsService,
      source: 'otp',
    });

    this.analyticsService.log({
      level: 'info',
      label: '[DOOD-3204] ComoOtpStep.validated',
      message: {
        next_step: nextStep,
      },
    });

    if (nextStep === 'complete') {
      this.goToComplete.next();
      return;
    }
    this.goToNextStep.next();
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.form.controls.authCode.value;
    this.form.controls.authCode.setValue(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.form.controls.authCode.value;
    this.form.controls.authCode.setValue(currentValue.slice(0, -1)); // Supprime le dernier caractère
  }

  private handleErrorResponse(error: any): void {
    this.isValidating = false;
    this.errorMessage = this.comoService.getErrorMessageFromCode(error.code);
    this.analyticsService.trackErrorDisplay(
      'otp_invalid',
      error.code?.toString(),
      this.errorMessage,
      'como_otp_step',
    );
  }
}
